import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

const KDS_URL = "https://kds.orderart.com.au";

/**
 * The Kitchen Display System, embedded rather than linked.
 *
 * Staff sign in and out using the KDS's own controls inside the frame - this panel deliberately
 * doesn't try to drive that. It is a different origin, so its session, cookies and DOM are all
 * closed to us; anything else would be a promise the browser will not keep. What the embed buys is
 * that none of it navigates the POS away, so a half-taken order is never lost to a trip to the KDS.
 *
 * Full screen tries the Fullscreen API first and falls back to filling the viewport with CSS. The
 * fallback is not a nicety: iOS Safari only grants requestFullscreen to <video>, so on an iPad -
 * which is most of the tablet fleet - the API path never runs.
 */
const KdsPanel = ({ description }) => {
  const navigate = useNavigate();
  const frameRef = useRef(null);
  const shellRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [usedFallback, setUsedFallback] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const fullscreenElement = () =>
    document.fullscreenElement || document.webkitFullscreenElement || null;

  const supportsFullscreenApi = () => {
    const el = shellRef.current;
    return !!(el && (el.requestFullscreen || el.webkitRequestFullscreen));
  };

  // The browser can leave fullscreen without us (Esc, the system gesture), so the button's label
  // follows the document rather than assuming our own click is the only way out.
  useEffect(() => {
    const sync = () => {
      if (!usedFallback) setIsFullscreen(!!fullscreenElement());
    };
    document.addEventListener("fullscreenchange", sync);
    document.addEventListener("webkitfullscreenchange", sync);
    return () => {
      document.removeEventListener("fullscreenchange", sync);
      document.removeEventListener("webkitfullscreenchange", sync);
    };
  }, [usedFallback]);

  // Esc exits the CSS fallback too, matching what the real fullscreen key already does.
  useEffect(() => {
    if (!usedFallback || !isFullscreen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        setIsFullscreen(false);
        setUsedFallback(false);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [usedFallback, isFullscreen]);

  const enterFullscreen = useCallback(async () => {
    const el = shellRef.current;
    if (!el) return;

    if (supportsFullscreenApi()) {
      try {
        await (el.requestFullscreen ? el.requestFullscreen() : el.webkitRequestFullscreen());
        setUsedFallback(false);
        setIsFullscreen(true);
        return;
      } catch {
        // Permissions policy or a user-gesture rule refused it - fall through to the CSS path
        // rather than leaving the button looking broken.
      }
    }
    setUsedFallback(true);
    setIsFullscreen(true);
  }, []);

  const exitFullscreen = useCallback(async () => {
    if (usedFallback) {
      setUsedFallback(false);
      setIsFullscreen(false);
      return;
    }
    try {
      if (document.exitFullscreen) await document.exitFullscreen();
      else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
    } catch {
      /* already out */
    }
    setIsFullscreen(false);
  }, [usedFallback]);

  /**
   * Back to the POS dashboard. Full screen is dropped first and deliberately awaited: the native
   * mode would otherwise still be showing this element as the browser tears it down, and the CSS
   * fallback is a fixed overlay that has to come down before anything behind it is reachable.
   * HardwarePage navigates without the cart guard, so this matches the rest of the page.
   */
  const backToPos = useCallback(async () => {
    if (isFullscreen) await exitFullscreen();
    navigate("/dashboard");
  }, [isFullscreen, exitFullscreen, navigate]);

  return (
    <div className={`kdsShell${isFullscreen && usedFallback ? " kdsShellFallbackFs" : ""}`} ref={shellRef}>
      <div className="kdsBar">
        <div className="kdsBarText">
          <h4 className="mb-1">Kitchen Display Systems</h4>
          {description && <p className="kdsBarDesc mb-0">{description}</p>}
        </div>

        <div className="kdsBarActions">
          <button
            type="button"
            className="btn btnLight btn-sm kdsBackBtn"
            onClick={backToPos}
            title="Leave the kitchen display and return to the POS dashboard"
          >
            &#8592; POS
          </button>
          <button
            type="button"
            className="btn btnLight btn-sm"
            onClick={() => setReloadKey((n) => n + 1)}
            title="Reload the kitchen display"
          >
            Reload
          </button>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={isFullscreen ? exitFullscreen : enterFullscreen}
          >
            {isFullscreen ? "Exit full screen" : "Full screen"}
          </button>
        </div>
      </div>

      <div className="kdsFrameWrap">
        <iframe
          key={reloadKey}
          ref={frameRef}
          className="kdsFrame"
          src={KDS_URL}
          title="Kitchen Display System"
        />
      </div>
    </div>
  );
};

export default KdsPanel;
